import React, { useState } from 'react';
import { Box, Typography, FormControl, InputLabel, Select, MenuItem, Grid, Button, TextField, IconButton, Slider } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { AllSideTabs } from '../../../constants';
import { useLogger } from '../../../contexts/LoggerContext';
import { useDepthChart } from '../../../contexts/DepthChartContext';
import { PlayLog, GameLog } from '../../../types/LogTypes';

// Define the possible states
type KORState = 
  | 'INITIAL'
  | 'TOUCHBACK'
  | 'OOB'
  | 'OOB_CHOICE'
  | 'REKICK'
  | 'RETURNER_SELECT'
  | 'RETURN_YARDAGE'
  | 'RETURN_PENALTY'
  | 'RETURN_FUMBLE';

// Define the state machine type
type KORStateMachine = {
  [key in KORState]: {
    previousState: KORState | null;
    nextStates: KORState[];
    title: string;
  };
};

// Define the state machine
const stateMachine: KORStateMachine = {
  INITIAL: {
    previousState: null,
    nextStates: ['TOUCHBACK', 'OOB', 'RETURNER_SELECT'],
    title: 'Kickoff Result'
  },
  TOUCHBACK: {
    previousState: 'INITIAL',
    nextStates: ['INITIAL'], // Will transition to offense in parent
    title: 'Touchback'
  },
  OOB: {
    previousState: 'INITIAL',
    nextStates: ['OOB_CHOICE'],
    title: 'Kick Out of Bounds'
  },
  OOB_CHOICE: {
    previousState: 'OOB',
    nextStates: ['REKICK', 'INITIAL'], // Will transition to offense in parent
    title: 'Out of Bounds Choice'
  },
  REKICK: {
    previousState: 'OOB_CHOICE',
    nextStates: ['INITIAL'],
    title: 'Re-kick'
  },
  RETURNER_SELECT: {
    previousState: 'INITIAL',
    nextStates: ['RETURN_YARDAGE', 'INITIAL'], // Allow going back to initial
    title: 'Select Returner'
  },
  RETURN_YARDAGE: {
    previousState: 'RETURNER_SELECT',
    nextStates: ['RETURNER_SELECT', 'INITIAL', 'RETURN_PENALTY', 'RETURN_FUMBLE'],
    title: 'Return Yardage'
  },
  RETURN_PENALTY: {
    previousState: 'RETURN_YARDAGE',
    nextStates: ['INITIAL', 'RETURN_YARDAGE'],
    title: 'Complete Return with Penalty'
  },
  RETURN_FUMBLE: {
    previousState: 'RETURN_YARDAGE',
    nextStates: ['INITIAL'],
    title: 'Fumble'
  }
};

const KOR_PLAYS = [
  'Red Sea',
  'Left Return',
  'Matchup',
  'Gash',
  'Alley'
];

// Add these types and constants before the KORLogger component
type YardlinePosition = {
  value: number;
  label: string;
};

const YARDLINE_POSITIONS: YardlinePosition[] = [
  { value: 0, label: 'Endzone' },
  ...Array.from({ length: 49 }, (_, i) => ({ value: -(i + 1), label: `${-(i + 1)}` })),
  { value: 50, label: '50' },
  ...Array.from({ length: 49 }, (_, i) => ({ value: 49 - i, label: `${49 - i}` }))
];

const calculateReturnYardage = (caughtOn: number, returnedTo: number): number => {
  const caughtInEndzone = caughtOn === 0;
  const caughtInOwnTerritory = caughtOn < 0;
  const returnedToOwnTerritory = returnedTo < 0;
  const returnedToAbs = Math.abs(returnedTo);
  const caughtOnAbs = 50 - Math.abs(caughtOn);
  const returnedToAbsFrom50 = 50 - returnedToAbs;

  // handle caught in endzone first
  if (caughtInEndzone && returnedToOwnTerritory) {
    return returnedToAbs;
  }
  if (caughtInEndzone && !returnedToOwnTerritory) {
    return returnedToAbsFrom50 + 50;
  }

  // handle caught in own territory
  if (caughtInOwnTerritory && returnedToOwnTerritory) {
    return caughtOnAbs - returnedToAbsFrom50;
  }
  if (caughtInOwnTerritory && !returnedToOwnTerritory) {
    return returnedToAbsFrom50 + caughtOnAbs;
  }

  // handle caught in opponent territory
  if (!caughtInOwnTerritory && returnedToOwnTerritory) {
    return (caughtOnAbs + returnedToAbsFrom50) * -1;
  }
  if (!caughtInOwnTerritory && !returnedToOwnTerritory) {
    return returnedToAbsFrom50 - caughtOnAbs;
  }
  
  
  return 0;
};

const YardlineSlider: React.FC<{
  value: number;
  onChange: (value: number) => void;
  label: string;
}> = ({ value, onChange, label }) => {
  // Create an array of indices that maps to the correct order of yardlines
  const orderedIndices = YARDLINE_POSITIONS.map((_, index) => index);
  
  // Find the current index in our ordered list
  const currentIndex = YARDLINE_POSITIONS.findIndex(pos => pos.value === value);

  // Only label every 10th yardline, Endzone, and 50
  const marks = YARDLINE_POSITIONS.map((pos, index) => {
    if (
      pos.value === 0 ||
      pos.value === 50 ||
      pos.value === -40 ||
      pos.value === -30 ||
      pos.value === -20 ||
      pos.value === -10 ||
      pos.value === 40 ||
      pos.value === 30 ||
      pos.value === 20 ||
      pos.value === 10 ||
      pos.value === 1
    ) {
      return { value: index, label: pos.label };
    }
    return { value: index };
  });

  // Find the label for the current value
  const currentLabel = YARDLINE_POSITIONS[currentIndex]?.label ?? value.toString();

  return (
    <Box sx={{ width: '100%', px: 2 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 1 }}>
        <Typography id={`${label}-slider-label`} gutterBottom>
          {label}
        </Typography>
        <Typography variant="subtitle1" sx={{ mt: 0.5, fontWeight: 'bold' }}>
          {currentLabel}
        </Typography>
      </Box>
      <Slider
        value={currentIndex}
        onChange={(_event, newValue) => {
          const newIndex = newValue as number;
          onChange(YARDLINE_POSITIONS[newIndex].value);
        }}
        aria-labelledby={`${label}-slider-label`}
        step={null}
        marks={marks}
        min={0}
        max={YARDLINE_POSITIONS.length - 1}
        valueLabelDisplay="auto"
        valueLabelFormat={(index) => YARDLINE_POSITIONS[index].label}
        sx={{
          '& .MuiSlider-markLabel': {
            fontSize: '0.75rem',
          },
          '& .MuiSlider-valueLabel': {
            fontSize: '0.875rem',
          }
        }}
      />
    </Box>
  );
};

// Separate view components for each state
const InitialView: React.FC<{
  onStateChange: (state: KORState) => void;
  kickoffFrom: number;
  setKickoffFrom: (value: number) => void;
  selectedPlayCall: string;
  setSelectedPlayCall: (value: string) => void;
}> = ({ onStateChange, kickoffFrom, setKickoffFrom, selectedPlayCall, setSelectedPlayCall }) => (
  <Box sx={{ p: 2 }}>
    <Grid container spacing={2} alignItems="center" mb={3} mt={2}>
      <Grid item xs={12} sm={6}>
        <TextField
          label="Kickoff From (Yard Line)"
          type="number"
          value={kickoffFrom}
          onChange={e => setKickoffFrom(Number(e.target.value))}
          inputProps={{ min: 1, max: 50 }}
          fullWidth
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth>
          <InputLabel id="kor-play-select-label">Play Call</InputLabel>
          <Select
            labelId="kor-play-select-label"
            id="kor-play-select"
            value={selectedPlayCall}
            label="Play Call"
            onChange={e => setSelectedPlayCall(e.target.value)}
          >
            {KOR_PLAYS.map((play) => (
              <MenuItem key={play} value={play}>
                {play}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
    </Grid>
    <Grid container spacing={3} justifyContent="center">
      <Grid item xs={12} sm={4}>
        <Button
          variant="contained"
          color="warning"
          fullWidth
          sx={{ height: 70, fontSize: 22, fontWeight: 'bold', borderRadius: 2 }}
          onClick={() => onStateChange('OOB')}
        >
          KICK OUT OF BOUNDS
        </Button>
      </Grid>
      <Grid item xs={12} sm={4}>
        <Button
          variant="contained"
          color="info"
          fullWidth
          sx={{ height: 70, fontSize: 22, fontWeight: 'bold', borderRadius: 2 }}
          onClick={() => onStateChange('TOUCHBACK')}
        >
          TOUCHBACK
        </Button>
      </Grid>
      <Grid item xs={12} sm={4}>
        <Button
          variant="contained"
          color="success"
          fullWidth
          sx={{ height: 70, fontSize: 22, fontWeight: 'bold', borderRadius: 2 }}
          onClick={() => onStateChange('RETURNER_SELECT')}
        >
          RETURNED
        </Button>
      </Grid>
    </Grid>
  </Box>
);

const ReturnerView: React.FC<{
  onStateChange: (state: KORState) => void;
  returner: number | null;
  setReturner: (value: number) => void;
  korReturnPlayers: any[];
}> = ({ onStateChange, returner, setReturner, korReturnPlayers }) => (
  <Box sx={{ p: 2 }}>
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        mb: 2,
        width: '100%',
        position: 'relative',
        minHeight: 64,
      }}
    >
      <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-start' }}>
        <IconButton onClick={() => onStateChange('INITIAL')}>
          <ChevronLeftIcon fontSize="large" />
        </IconButton>
      </Box>
      <Box sx={{ flex: 2, display: 'flex', justifyContent: 'center' }}>
        <Typography variant="h6" gutterBottom>
          Select Returner
        </Typography>
      </Box>
      <Box sx={{ flex: 1 }} />
    </Box>
    <Grid container spacing={2}>
      {korReturnPlayers.map((player) => (
        <Grid item xs={4} key={player.id}>
          <Button
            variant={returner === player.id ? "contained" : "outlined"}
            onClick={() => {
              setReturner(player.id);
              onStateChange('RETURN_YARDAGE');
            }}
            sx={{
              width: '100%',
              height: '80px',
              fontSize: '24px',
              fontWeight: 'bold',
              borderRadius: '8px',
              border: '2px solid',
              '&:hover': {
                transform: 'scale(1.05)',
                transition: 'transform 0.2s',
              },
            }}
          >
            {player.jersey_number || '?'}
          </Button>
        </Grid>
      ))}
    </Grid>
  </Box>
);

const OOBChoiceView: React.FC<{
  onStateChange: (state: KORState) => void;
}> = ({ onStateChange }) => (
  <Box sx={{ p: 2 }}>
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        mb: 2,
        width: '100%',
        position: 'relative',
        minHeight: 64,
      }}
    >
      <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-start' }}>
        <IconButton onClick={() => onStateChange('OOB')}>
          <ChevronLeftIcon fontSize="large" />
        </IconButton>
      </Box>
      <Box sx={{ flex: 2, display: 'flex', justifyContent: 'center' }}>
        <Typography variant="h6" gutterBottom>
          Out of Bounds Choice
        </Typography>
      </Box>
      <Box sx={{ flex: 1 }} />
    </Box>
    <Grid container spacing={3} justifyContent="center">
      <Grid item xs={12} sm={6}>
        <Button
          variant="contained"
          color="primary"
          fullWidth
          sx={{ height: 70, fontSize: 22, fontWeight: 'bold', borderRadius: 2 }}
          onClick={() => onStateChange('REKICK')}
        >
          RE-KICK
        </Button>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Button
          variant="contained"
          color="secondary"
          fullWidth
          sx={{ height: 70, fontSize: 22, fontWeight: 'bold', borderRadius: 2 }}
          onClick={() => onStateChange('INITIAL')}
        >
          BALL ON 40
        </Button>
      </Grid>
    </Grid>
  </Box>
);

const GenericReturnView: React.FC<{ title: string; onStateChange: (state: KORState) => void }> = ({ title, onStateChange }) => (
  <Box sx={{ p: 2 }}>
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        mb: 2,
        width: '100%',
        position: 'relative',
        minHeight: 64,
      }}
    >
      <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-start' }}>
        <IconButton onClick={() => onStateChange('RETURN_YARDAGE')}>
          <ChevronLeftIcon fontSize="large" />
        </IconButton>
      </Box>
      <Box sx={{ flex: 2, display: 'flex', justifyContent: 'center' }}>
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
      </Box>
      <Box sx={{ flex: 1 }} />
    </Box>
  </Box>
);

const ReturnYardageView: React.FC<{
  onStateChange: (state: KORState) => void;
  returner: number | null;
  kickoffFrom: number;
  selectedPlayCall: string;
  gameQuarter: number;
  gameTimeRemaining: string;
  gameClubScore: number;
  gameOppScore: number;
}> = ({ onStateChange, returner, kickoffFrom, selectedPlayCall, gameQuarter, gameTimeRemaining, gameClubScore, gameOppScore }) => {
  const [ballCaughtOn, setBallCaughtOn] = useState<number>(0);
  const [ballReturnedTo, setBallReturnedTo] = useState<number>(-20);
  const returnYardage = calculateReturnYardage(ballCaughtOn, ballReturnedTo);
  const { korReturnPlayers } = useDepthChart();
  const { 
    setSelectedTab,
    setSelectedSide,
    setBallOn,
    setDown,
    setDistance,
    setClubPossession,
    setGameLog,
    gameLog,
    selectedGame,
   } = useLogger();

   const getPlayerById = (id: number|null) => {
    if (id) {
      return korReturnPlayers.find((player) => player.id === id);
    }
    return {
      id: 0,
      name: 'Unknown',
      roster_membership_id: 0,
      jersey_number: 0,
      status: 'Unknown',
      position: 'Unknown',
      position_group: 'Unknown',
      player: {
        id: 0,
        name: 'Unknown',
        offensive_position: 'Unknown',
        defensive_position: 'Unknown',
        position_group: 'Unknown',
      },
    };
   }

  const handleCompleteReturn = () => {
    const returnerPlayer = getPlayerById(returner);
    if (!returnerPlayer) {
      console.error('No returner selected');
      return;
    }

    const playLogIndex = gameLog?.play_logs.length ?? 0;

    const playLog: PlayLog = {
      index: playLogIndex + 1,
      play: {
        returner: returnerPlayer,
        kickoffFrom: kickoffFrom,
        kickoffTo: ballReturnedTo,
        kickoffYardage: returnYardage,
        playCall: selectedPlayCall,
        qtr: gameQuarter,
        time: gameTimeRemaining,
        clubScore: gameClubScore,
        oppScore: gameOppScore,
        ballOn: ballReturnedTo,
      },
    };

    // Create new GameLog object
    const newGameLog: GameLog = {
      game_id: selectedGame?.id ?? 0,
      play_logs: [...(gameLog?.play_logs ?? []), playLog]
    };

    setGameLog(newGameLog);

    setSelectedTab(AllSideTabs.findIndex((tab) => tab.value === 'OFFENSE'));
    setSelectedSide('OFFENSE');
    onStateChange('INITIAL');
    setBallOn(ballReturnedTo);
    setDown(1);
    setDistance(10);
    setClubPossession();
  }

  
  return (
    <Box sx={{ p: 2 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          mb: 2,
          width: '100%',
          position: 'relative',
          minHeight: 64,
        }}
      >
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-start' }}>
          <IconButton onClick={() => onStateChange('RETURNER_SELECT')}>
            <ChevronLeftIcon fontSize="large" />
          </IconButton>
        </Box>
        <Box sx={{ flex: 2, display: 'flex', justifyContent: 'center' }}>
          <Typography variant="h6" gutterBottom>
            Return Yardage
          </Typography>
        </Box>
        <Box sx={{ flex: 1 }} />
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <YardlineSlider
            value={ballCaughtOn}
            onChange={setBallCaughtOn}
            label="Ball Caught On"
          />
        </Grid>
        <Grid item xs={12}>
          <YardlineSlider
            value={ballReturnedTo}
            onChange={setBallReturnedTo}
            label="Ball Returned To"
          />
        </Grid>
        <Grid item xs={12}>
          <Typography variant="h6" align="center" gutterBottom>
            Return Yardage: {returnYardage} yards
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleCompleteReturn}
              disabled={!returner}
              fullWidth
            >
              Complete Return
            </Button>
            <Button
              variant="contained"
              color="warning"
              onClick={() => onStateChange('RETURN_PENALTY')}
              disabled={!returner}
              fullWidth
            >
              Complete Return with Penalty
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={() => onStateChange('RETURN_FUMBLE')}
              disabled={!returner}
              fullWidth
            >
              Fumble
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

interface KORLoggerProps {
}

export const KORLogger: React.FC<KORLoggerProps> = () => {
  const [currentState, setCurrentState] = useState<KORState>('INITIAL');
  const [kickoffFrom, setKickoffFrom] = useState<number>(-35);
  const [selectedPlayCall, setSelectedPlayCall] = useState<string>('');
  const [returner, setReturner] = useState<number | null>(null);
  const { korReturnPlayers } = useDepthChart();
  const { 
    quarter: gameQuarter,
    timeRemaining: gameTimeRemaining,
    clubScore: gameClubScore,
    oppScore: gameOppScore,
  } = useLogger();

  const handleStateChange = (newState: KORState) => {
    // Validate the transition is allowed
    if (stateMachine[currentState].nextStates.includes(newState)) {
      setCurrentState(newState);
    }
  };

  const renderCurrentState = () => {
    switch (currentState) {
      case 'INITIAL':
        return (
          <InitialView
            onStateChange={handleStateChange}
            kickoffFrom={kickoffFrom}
            setKickoffFrom={setKickoffFrom}
            selectedPlayCall={selectedPlayCall}
            setSelectedPlayCall={setSelectedPlayCall}
          />
        );
      case 'RETURNER_SELECT':
        return (
          <ReturnerView
            onStateChange={handleStateChange}
            returner={returner}
            setReturner={setReturner}
            korReturnPlayers={korReturnPlayers}
          />
        );
      case 'OOB_CHOICE':
        return <OOBChoiceView onStateChange={handleStateChange} />;
      case 'RETURN_YARDAGE':
        return (
          <ReturnYardageView 
            onStateChange={handleStateChange} 
            returner={returner}
            kickoffFrom={kickoffFrom}
            selectedPlayCall={selectedPlayCall}
            gameQuarter={gameQuarter}
            gameTimeRemaining={gameTimeRemaining}
            gameClubScore={gameClubScore}
            gameOppScore={gameOppScore}
          />
        );
      case 'RETURN_PENALTY':
        return <GenericReturnView title="Complete Return with Penalty" onStateChange={handleStateChange} />;
      case 'RETURN_FUMBLE':
        return <GenericReturnView title="Fumble" onStateChange={handleStateChange} />;
      case 'TOUCHBACK':
      case 'OOB':
      case 'REKICK':
        // These states will be handled by the parent component
        // to transition to offense or handle the specific outcome
        return null;
      default:
        return null;
    }
  };

  return (
    <Box>
      {renderCurrentState()}
    </Box>
  );
};